/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import {
  LeadRestService,
  SalesManagementCalculationType,
  UserRestService,
} from '@gh/builder-shared-data';
import {
  DictionaryService,
  InitViewService,
} from '@gh/common-shared';
import { FilteredDataQuery, getSlicedData, SlicedDataSource } from '@gh/core-data';
import { ID } from '@gh/common-shared-data';
import { GridLayout, GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';

import { Observable } from 'rxjs';

import {
  LeadListItem,
  SalesManagementReportFilter,
  salesManagementReportFilterFromDto,
  salesManagementReportFilterToDto,
} from './lead.model';
import {
  isSalespersonSelectItemInactive,
  SalespersonSelectItem,
  salespersonSelectItemMapper,
  UserListItem,
} from './lead-board.model';
import { concat, map, partition, reverse, sortBy } from 'lodash';
import {
  BuilderUserContext,
  DEFAULT_FILTER_STATE,
  FilterNames,
  resolveLeadsListFields,
} from '@gh/builder-shared';
import { of$ } from '@gh/rx';
import {
  getDefaultLeadsListSalesPersonId,
  parseLeadsListFilterFromRoute,
  peekLeadsListPageReload,
} from '@gh/builder-shared';

@Injectable()
export class LeadListBusinessService implements InitViewService {
  constructor(private leadRest: LeadRestService,
              private dictionaryService: DictionaryService,
              private userRest: UserRestService,
              private gridLayoutService: GridLayoutService,
              private userContext: BuilderUserContext) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    const routeFilter = peekLeadsListPageReload() ? null : parseLeadsListFilterFromRoute(route);
    const salesPersonId = (routeFilter?.salesPersonId as string | undefined)
      || getDefaultLeadsListSalesPersonId(this.userContext);
    return {
      gridLayout: this.gridLayoutService.get('leads'),
      filter: of$(DEFAULT_FILTER_STATE[FilterNames.leads]),
      salesPersonsList: this.findAllSalesPersons(),
      salesFolderStatuses: this.dictionaryService.getDictionaryFromGlobal('SALES_FOLDER_STATUS'),
      salesPersonId,
    };
  }

  initSalesManagementReportFilterDialog(): any {
    return {
      reportFilter: this.initReportFilter(),
      salesManagementCalculationTypes: this.dictionaryService.getByEnum(SalesManagementCalculationType),
    };
  }

  findAllAsDataSource(filter?: any, layout?: GridLayout): SlicedDataSource<LeadListItem> {
    return {
      select: (query) => {
        const fields = resolveLeadsListFields(layout, filter || {}, query.sorting?.field);
        return this.leadRest.findAllAsDataSource({ ...filter, fields }).select(query);
      },
    };
  }

  initReportFilter(): Observable<SalesManagementReportFilter> {
    return this.leadRest.initReportFilter().pipe(map$(salesManagementReportFilterFromDto));
  }

  updateReportFilter(reportFilter: SalesManagementReportFilter): Observable<void> {
    return this.leadRest.updateReportFilter(salesManagementReportFilterToDto(reportFilter));
  }
  findAllSalesPersons(): Observable<SalespersonSelectItem[]> {
    return this.userRest.findAllSalesPersons(true).pipe(
      map$((userList: UserListItem[]) =>
             concat(
               [],
               ...reverse(
                 partition(
                   sortBy(
                     map(userList, salespersonSelectItemMapper),
                     'name',
                   ),
                   isSalespersonSelectItemInactive)))));
  }

  findSalesPersonsByName(query: FilteredDataQuery<string>, filter: any): Observable<{ id: ID; name: string }[]> {
    return this.userRest.findSalespersonsAsDataSource({
      ...filter,
      searchString: query.filter,
    }).select(query).pipe(map$(getSlicedData), map$((res: any[]) => res.map((user) => ({
      id: user.id || user.userId,
      name: user.name || user.fullName || user.userName || user.contact?.name || user.userEmail || 'Unknown',
    }))));
  }

}
